import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Application } from '../api/types'
import { sectionBySlug } from '../apply/sections'

export type UploadState = 'idle' | 'uploading' | 'processing' | 'success' | 'error'

export type SaveState = 'idle' | 'saving' | 'saved' | 'error'

export type OwnerType = 'individual' | 'holding_company'

export interface OwnerStoreEntry {
  id: string
  ownerType: OwnerType
  // Individual fields
  name?: string
  dob?: string
  nationality?: string
  passportNumber?: string
  placeOfIssue?: string
  ownershipPct?: number
  // Holding company fields
  companyName?: string
  countryOfIncorporation?: string
}

export interface DirectorStoreEntry {
  id: string
  name?: string
  dob?: string
  nationality?: string
  passportNumber?: string
  placeOfIssue?: string
}

interface FormState {
  currentStep: number
  authEmail: string
  formData: Record<string, unknown>
  autoFilledFields: string[]
  uploadStates: Record<string, UploadState>
  // New typed fields
  companyWebsite: string
  licenseType: string
  licenseNumber: string
  // Submission tracking
  submittedAt: string | null
  // MAF 15.05: inline manual-fallback disclaimer fires only the first time
  // the user encounters an upload zone in a session.
  hasSeenManualFallbackInline: boolean
  // Onboarding welcome wizard: re-appears after every email verification
  // (pendingWelcomeWizard is set on verify), until the applicant explicitly
  // hits Skip (wizardSkipped), which suppresses it for good.
  pendingWelcomeWizard: boolean
  wizardSkipped: boolean
  // Auto-save status surfaced by SaveIndicator; driven by useAutoSave.
  saveState: SaveState

  // Actions
  setField: (name: string, value: unknown) => void
  /** Bridge the session account's email into the form (magic-link logins never
   *  pass through LoginScreen, which is the only other place that sets it). */
  seedAuthEmail: (email: string) => void
  setAutoFilled: (fields: string[]) => void
  setStep: (step: number) => void
  setUploadState: (slot: string, state: UploadState) => void
  markSubmitted: (at?: string) => void
  markManualFallbackSeen: () => void
  triggerWelcomeWizard: () => void
  dismissWelcomeWizard: () => void
  skipWelcomeWizard: () => void
  clearForm: () => void
  reset: () => void
  setSaveState: (state: SaveState) => void
  hydrateFromServer: (application: Application | null) => void
}

const initialState = {
  currentStep: 0,
  authEmail: '',
  formData: {},
  autoFilledFields: [],
  uploadStates: {},
  companyWebsite: '',
  licenseType: '',
  licenseNumber: '',
  submittedAt: null as string | null,
  hasSeenManualFallbackInline: false,
  pendingWelcomeWizard: false,
  wizardSkipped: false,
  saveState: 'idle' as SaveState,
}

export const useFormStore = create<FormState>()(
  persist(
    (set) => ({
      ...initialState,

      setField: (name, value) =>
        set((state) => ({
          formData: { ...state.formData, [name]: value },
        })),

      seedAuthEmail: (email) =>
        set((state) => {
          // No-op when already present so the auto-save baseline stays clean.
          if (state.authEmail === email && state.formData.authEmail === email) return state
          return {
            authEmail: email,
            formData: { ...state.formData, authEmail: email },
          }
        }),

      setAutoFilled: (fields) =>
        set({ autoFilledFields: fields }),

      setStep: (step) =>
        set({ currentStep: step }),

      setUploadState: (slot, uploadState) =>
        set((state) => ({
          uploadStates: { ...state.uploadStates, [slot]: uploadState },
        })),

      markSubmitted: (at) =>
        set({ submittedAt: at ?? new Date().toISOString() }),

      markManualFallbackSeen: () =>
        set({ hasSeenManualFallbackInline: true }),

      triggerWelcomeWizard: () =>
        set({ pendingWelcomeWizard: true }),

      dismissWelcomeWizard: () =>
        set({ pendingWelcomeWizard: false }),

      skipWelcomeWizard: () =>
        set({ pendingWelcomeWizard: false, wizardSkipped: true }),

      // Wipe all filled fields and uploads but keep the user signed in —
      // used by the "Clear form" button in the header for fresh UX test runs.
      clearForm: () =>
        set((state) => ({
          ...initialState,
          authEmail: state.authEmail,
          formData: state.formData.authEmail
            ? { authEmail: state.formData.authEmail }
            : {},
        })),

      reset: () => set(initialState),

      setSaveState: (saveState) => set({ saveState }),

      // Server form_data is the source of truth once a session exists. The
      // backend can only null-out keys (jsonb || merge can't delete), so nulls
      // are dropped here to look like absent fields.
      hydrateFromServer: (application) =>
        set(() => {
          if (!application) return { ...initialState }
          const formData: Record<string, unknown> = {}
          for (const [key, value] of Object.entries(application.form_data ?? {})) {
            // The auto-save round-trips these two store fields through
            // form_data; keep them out of the plain field bag.
            if (key === 'autoFilledFields' || key === 'uploadStates') continue
            if (value !== null) formData[key] = value
          }
          return {
            formData,
            currentStep: sectionBySlug(application.current_section)?.step ?? 0,
            submittedAt: application.submitted_at,
            autoFilledFields: (formData['autoFilledFields'] as string[] | undefined) ?? [],
            uploadStates: (formData['uploadStates'] as Record<string, UploadState> | undefined) ?? {},
          }
        }),
    }),
    {
      name: 'zota-form-state',
      // Only UX-continuity flags persist locally; applicant form data lives on
      // the server (and would otherwise linger in localStorage on shared
      // machines after logout).
      partialize: (state) => ({
        authEmail: state.authEmail,
        // Keep the email across a reload of /verify (screens read it from
        // formData); everything else in formData comes from the server.
        formData: state.formData['authEmail']
          ? { authEmail: state.formData['authEmail'] }
          : {},
        hasSeenManualFallbackInline: state.hasSeenManualFallbackInline,
        wizardSkipped: state.wizardSkipped,
        pendingWelcomeWizard: state.pendingWelcomeWizard,
      }),
    }
  )
)
